import { QueryClient, useMutation, useQuery, UseQueryOptions } from '@tanstack/react-query';

import { ENDPOINTS } from '@/api/endpoints';
import { fetchClient } from '@/api/fetch-client';
import {
  GetAddendaError,
  GetAddendaRequest,
  GetAddendaResponse,
  PatchAddendumError,
  PatchAddendumRequest,
  PatchAddendumResponse,
  PostAddendumRevertError,
  PostAddendumRevertRequest,
  PostAddendumRevertResponse,
} from '@/api/model';

const KEY = {
  GET_ADDENDA_BY_REPORT: 'GET_ADDENDA_BY_REPORT',
} as const;

const request = {
  getAddenda: async ({ payload: { studyId } }: GetAddendaRequest, signal?: AbortSignal) => {
    const { data } = await fetchClient.FCT.get<GetAddendaResponse>(
      `${ENDPOINTS.FCT.ADDENDUM.STUDY}${studyId}`,
      { signal },
    );

    return data;
  },
  patchAddendum: async ({ payload }: PatchAddendumRequest) => {
    const { data } = await fetchClient.FCT.patch<PatchAddendumResponse>(
      `${ENDPOINTS.FCT.ADDENDUM.BASE}${payload.uuid}`,
      { body: { summary: payload.summary } },
    );

    return data;
  },
  postAddendumRevert: async ({
    payload: { reportUuid, ...payload },
  }: PostAddendumRevertRequest) => {
    const { data } = await fetchClient.FCT.post<PostAddendumRevertResponse>(
      `${ENDPOINTS.FCT.ADDENDUM.REPORT}${reportUuid}/revert`,
      {
        body: payload,
      },
    );

    return data;
  },
};

const query = {
  useGetAddenda: (
    { payload }: GetAddendaRequest,
    config?: Partial<UseQueryOptions<GetAddendaResponse, GetAddendaError>>,
  ) =>
    useQuery<GetAddendaResponse, GetAddendaError>({
      enabled: !!payload.studyId,
      queryFn: ({ signal }) => request.getAddenda({ payload }, signal),
      queryKey: [KEY.GET_ADDENDA_BY_REPORT, payload.studyId],
      ...config,
    }),
};

const mutation = {
  usePatchAddendum: (queryClient: QueryClient) =>
    useMutation<PatchAddendumResponse, PatchAddendumError, PatchAddendumRequest>({
      mutationFn: request.patchAddendum,
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: [KEY.GET_ADDENDA_BY_REPORT] });
      },
    }),
  usePostAddendumRevert: (queryClient: QueryClient) =>
    useMutation<PostAddendumRevertResponse, PostAddendumRevertError, PostAddendumRevertRequest>({
      mutationFn: request.postAddendumRevert,
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: [KEY.GET_ADDENDA_BY_REPORT] });
      },
    }),
};

export const addendum = {
  KEY,
  mutation,
  query,
  request,
};
